/**
Copyright (C) 2019-2025, Infraclear Inc. All Rights Reserved.
*/

import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  TextField,
  Alert,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import BlockIcon from '@mui/icons-material/Block';
import SearchIcon from '@mui/icons-material/Search';
import { ApiKey, IssueApiKeyResponse } from './types';
import { fetchApiKeys, issueApiKey, rotateApiKey, revokeApiKey } from './apiKeyService';
import ConfirmDialog from '../clients/ConfirmDialog';
import { SnackBarMessage } from '../../../components/snackBar/snackBar';
import './apiKeys.scss';

const statusColorMap: Record<string, 'success' | 'warning' | 'error' | 'default'> = {
  ACTIVE: 'success',
  REVOKED: 'error',
  ROTATED: 'warning',
};

const AdminApiKeys: React.FC = () => {
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(false);
  const [orgFilter, setOrgFilter] = useState('');
  const [searched, setSearched] = useState(false);
  const [newKey, setNewKey] = useState<IssueApiKeyResponse | null>(null);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTitle, setConfirmTitle] = useState('');
  const [confirmMessage, setConfirmMessage] = useState('');
  const [confirmAction, setConfirmAction] = useState<() => void>(() => () => {});

  const [msgConfig, setMsgConfig] = useState({
    type: '',
    message: '',
    snackFlag: false,
  });

  const loadKeys = (organizationName: string) => {
    if (!organizationName.trim()) return;
    setLoading(true);
    setSearched(true);
    setNewKey(null);
    fetchApiKeys(organizationName.trim())
      .then((data) => {
        setKeys(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        setMsgConfig({
          type: 'error',
          message: 'Failed to load API keys.',
          snackFlag: true,
        });
        setKeys([]);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleSearch = () => {
    loadKeys(orgFilter);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleIssueKey = () => {
    if (!orgFilter.trim()) {
      setMsgConfig({
        type: 'error',
        message: 'Please enter an organization name first.',
        snackFlag: true,
      });
      return;
    }
    issueApiKey({ organizationName: orgFilter.trim() })
      .then((response) => {
        setNewKey(response);
        setMsgConfig({
          type: 'success',
          message: 'API key issued successfully.',
          snackFlag: true,
        });
        loadKeys(orgFilter);
      })
      .catch(() => {
        setMsgConfig({
          type: 'error',
          message: 'Failed to issue API key.',
          snackFlag: true,
        });
      });
  };

  const handleRotateClick = (key: ApiKey) => {
    setConfirmTitle('Rotate API Key');
    setConfirmMessage(
      `Are you sure you want to rotate key "${key.keyName}"? The old key will be invalidated immediately — any consumer using it will lose access.`
    );
    setConfirmAction(() => () => {
      rotateApiKey({ keyId: key.id })
        .then((response) => {
          setNewKey(response);
          setMsgConfig({
            type: 'success',
            message: 'API key rotated successfully.',
            snackFlag: true,
          });
          loadKeys(orgFilter);
        })
        .catch(() => {
          setMsgConfig({
            type: 'error',
            message: 'Failed to rotate API key.',
            snackFlag: true,
          });
        })
        .finally(() => {
          setConfirmOpen(false);
        });
    });
    setConfirmOpen(true);
  };

  const handleRevokeClick = (key: ApiKey) => {
    setConfirmTitle('Revoke API Key');
    setConfirmMessage(
      `Are you sure you want to revoke key "${key.keyName}"? This action cannot be undone.`
    );
    setConfirmAction(() => () => {
      revokeApiKey({ keyId: key.id })
        .then(() => {
          setMsgConfig({
            type: 'success',
            message: 'API key revoked successfully.',
            snackFlag: true,
          });
          loadKeys(orgFilter);
        })
        .catch(() => {
          setMsgConfig({
            type: 'error',
            message: 'Failed to revoke API key.',
            snackFlag: true,
          });
        })
        .finally(() => {
          setConfirmOpen(false);
        });
    });
    setConfirmOpen(true);
  };

  const columns: GridColDef[] = [
    {
      field: 'keyName',
      headerName: 'Key Name',
      flex: 1,
      minWidth: 180,
    },
    {
      field: 'prefix',
      headerName: 'Prefix',
      width: 140,
    },
    {
      field: 'organizationName',
      headerName: 'Organization',
      flex: 1,
      minWidth: 180,
    },
    {
      field: 'createdDate',
      headerName: 'Created',
      width: 160,
      valueFormatter: (value: string) => {
        if (!value) return '';
        return new Date(value).toLocaleDateString();
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          color={statusColorMap[params.value] || 'default'}
          variant="outlined"
        />
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box>
          {params.row.status === 'ACTIVE' && (
            <>
              <Tooltip title="Rotate">
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => handleRotateClick(params.row)}
                  aria-label="rotate key"
                >
                  <AutorenewIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Revoke">
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => handleRevokeClick(params.row)}
                  aria-label="revoke key"
                >
                  <BlockIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>
      ),
    },
  ];

  return (
    <Box className="admin-api-keys">
      <Box className="admin-api-keys__header">
        <Typography variant="h5">API Key Management</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleIssueKey}
          disabled={!orgFilter.trim()}
        >
          Issue Key
        </Button>
      </Box>

      <Box className="admin-api-keys__filter">
        <TextField
          label="Organization Name"
          variant="outlined"
          size="small"
          value={orgFilter}
          onChange={(e) => setOrgFilter(e.target.value)}
          onKeyDown={handleKeyPress}
          sx={{ minWidth: 300 }}
        />
        <Button
          variant="outlined"
          startIcon={<SearchIcon />}
          onClick={handleSearch}
          disabled={!orgFilter.trim()}
        >
          Search
        </Button>
      </Box>

      {newKey?.rawKey && (
        <Alert severity="info" className="admin-api-keys__new-key-alert">
          <Typography variant="subtitle2">New API Key (copy now — it won't be shown again):</Typography>
          <Typography variant="body2" sx={{ fontFamily: 'monospace', mt: 0.5 }}>
            {newKey.rawKey}
          </Typography>
        </Alert>
      )}

      {loading ? (
        <Box className="admin-api-keys__loading">
          <CircularProgress />
        </Box>
      ) : searched ? (
        <Box className="admin-api-keys__grid">
          <DataGrid
            rows={keys}
            columns={columns}
            pageSizeOptions={[10, 25, 50]}
            initialState={{
              pagination: { paginationModel: { pageSize: 10 } },
            }}
            disableRowSelectionOnClick
            autoHeight
          />
        </Box>
      ) : null}

      <ConfirmDialog
        open={confirmOpen}
        title={confirmTitle}
        message={confirmMessage}
        onConfirm={confirmAction}
        onCancel={() => setConfirmOpen(false)}
      />

      {msgConfig.snackFlag && <SnackBarMessage snacBarValue={msgConfig} />}
    </Box>
  );
};

export default AdminApiKeys;
